/**
 * DataActions — Export / Import / Wipe All controls (Requirements 4.8,
 * 4.9, 6.6, 12.5).
 *
 * Surface:
 *   - "Export conversations" downloads the full `ExportBundle` as a
 *     pretty-printed JSON file (Requirement 4.8).
 *   - "Import conversations" parses the user-selected JSON file,
 *     forwards it to `ConversationStore.importAll`, and surfaces a
 *     toast with the exact `errorPath` on rejection (Requirement
 *     12.5). On success the page reloads so every Zustand slice
 *     re-hydrates from the freshly-imported data.
 *   - "Wipe all data" goes through a confirmation modal. After
 *     `wipeAll` resolves the page reloads so the boot sequence reseeds
 *     defaults and clears every slice's local cache (Requirement 6.6).
 *
 * The reload-after-import / reload-after-wipe is a deliberately simple
 * implementation. Future iterations can re-hydrate slices in place if
 * that becomes important.
 *
 * Validates: Requirements 4.8, 4.9, 6.6, 12.5.
 */

import { useState } from 'react';
import type { ChangeEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ImportResult } from '../../domain/types';
import { getActiveConversationStore } from '../../state';
import { Modal, Toast } from '../common';
import type { ToastTone } from '../common';

import styles from './DataActions.module.css';

interface ToastState {
  open: boolean;
  tone: ToastTone;
  message: string;
}

const INITIAL_TOAST: ToastState = {
  open: false,
  tone: 'info',
  message: '',
};

export function DataActions(): ReactElement {
  const { t } = useTranslation();
  const [confirmingWipe, setConfirmingWipe] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastState>(INITIAL_TOAST);

  const closeToast = (): void => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const handleExport = (): void => {
    void (async (): Promise<void> => {
      const store = getActiveConversationStore();
      const bundle = await store.exportAll();
      const text = JSON.stringify(bundle, null, 2);
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-companion-export-${today}.json`;
      // The link is synthesized in memory; no need to mount it in the
      // DOM. Some browsers still want a click event, so we dispatch
      // one via `.click()` directly on the disconnected element.
      a.click();
      // Revoke on the next tick so Chromium has a chance to start the
      // download before the URL becomes invalid.
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 0);
    })();
  };

  const handleImport = (file: File): void => {
    void (async (): Promise<void> => {
      let parsed: unknown;
      try {
        const text = await file.text();
        parsed = JSON.parse(text) as unknown;
      } catch {
        // Either the file couldn't be read or it wasn't valid JSON.
        // Both are reported to the user with the same neutral message
        // — the validator's `errorPath` only exists for structurally
        // valid JSON.
        setToast({
          open: true,
          tone: 'error',
          message: t('settings.data.importError'),
        });
        return;
      }

      const store = getActiveConversationStore();
      const result: ImportResult = await store.importAll(parsed);
      if (result.ok) {
        setToast({
          open: true,
          tone: 'success',
          message: t('settings.data.importSuccess'),
        });
        // Reload after the toast has had a moment to render so the user
        // sees the success cue. Slices re-hydrate on next boot.
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        setToast({
          open: true,
          tone: 'error',
          message: `${t('settings.data.importError')} (${result.errorPath})`,
        });
      }
    })();
  };

  const handleImportChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file !== undefined) {
      handleImport(file);
    }
    // Reset the input so picking the same file twice still triggers
    // a change event the second time.
    e.target.value = '';
  };

  const handleWipe = (): void => {
    void (async (): Promise<void> => {
      const store = getActiveConversationStore();
      await store.wipeAll();
      setConfirmingWipe(false);
      setToast({
        open: true,
        tone: 'success',
        message: t('settings.data.wipeAllSuccess'),
      });
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    })();
  };

  return (
    <section
      className={styles.section}
      aria-labelledby="data-heading"
      data-testid="data-actions"
    >
      <h2 id="data-heading" className={styles.heading}>
        {t('settings.data.title')}
      </h2>
      <div className={styles.actions}>
        <button type="button" onClick={handleExport} className={styles.btn}>
          {t('settings.data.export')}
        </button>
        <label className={styles.btn}>
          <span>{t('settings.data.import')}</span>
          <input
            type="file"
            accept="application/json,.json"
            onChange={handleImportChange}
            className={styles.fileInput}
          />
        </label>
        <button
          type="button"
          onClick={() => setConfirmingWipe(true)}
          className={[styles.btn, styles.btnDestructive].join(' ')}
        >
          {t('settings.data.wipeAll')}
        </button>
      </div>

      <Modal
        open={confirmingWipe}
        title={t('settings.data.wipeAll')}
        onClose={() => setConfirmingWipe(false)}
        primaryAction={{
          label: t('settings.data.wipeAll'),
          tone: 'destructive',
          onClick: handleWipe,
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setConfirmingWipe(false),
        }}
      >
        <p className={styles.modalBody}>{t('settings.data.wipeAllConfirm')}</p>
      </Modal>

      <Toast
        open={toast.open}
        tone={toast.tone}
        message={toast.message}
        onClose={closeToast}
      />
    </section>
  );
}
